/**
 * Copyright © 2005 Martin Kimmings
 * All rights reserved
 * 
 * Timing utilities - Provides TimeStep and Frequency classes
 * Converted from Delphi/Pascal to TypeScript
 */

/**
 * A TimeStep is used to keep track of discrete steps in time.
 * The client of the time step would poll the time step to determine
 * when to update game logic while maintaining smooth rendering.
 */
export class TimeStep {
    /** The start of the current step */
    private startTime: number = 0;
    
    /** The TimeStep in milliseconds */
    private timeStepInMilliseconds: number;
    
    /** Where we are in the current time step, often used as an interpolation value (0-1) */
    private stepFraction: number = 0;

    constructor(timeStepInMilliseconds: number = 33) {
        this.timeStepInMilliseconds = timeStepInMilliseconds;
    }

    /** Starts/initializes the time step */
    start(): void {
        this.startTime = Date.now();
    }

    /** Moves us into the next time step */
    inc(): void {
        this.startTime = this.startTime + this.timeStepInMilliseconds;
    }

    /** Should we be in the next time step? */
    isPending(): boolean {
        const tickCount = Date.now();
        const timeLapse = tickCount - this.startTime;
        const result = timeLapse > this.timeStepInMilliseconds;
        
        if (!result) {
            // A step isn't pending but remember where we are in this step
            this.stepFraction = timeLapse / this.timeStepInMilliseconds;
        }
        
        return result;
    }

    /** Where we are in the current time step (0-1 for interpolation) */
    getStepFraction(): number {
        return this.stepFraction;
    }

    /** Get the timestep duration in milliseconds */
    getTimeStep(): number {
        return this.timeStepInMilliseconds;
    }
}

/**
 * A Frequency is used to calculate the FPS - based on how often
 * calcFPS is called
 */
export class Frequency {
    private fps: number = 0;
    private frameCount: number = 0;
    private referenceTime: number = 0;

    /** Call this once per frame to calculate FPS */
    calcFPS(): void {
        const frameTime = Date.now();
        this.frameCount++;
        
        if (this.referenceTime === 0) {
            this.referenceTime = frameTime;
        }

        if (frameTime - this.referenceTime >= 1000) {
            this.fps = this.frameCount / ((frameTime - this.referenceTime) / 1000);
            this.referenceTime = 0;
            this.frameCount = 0;
        }
    }

    /** Get the current FPS result */
    getResult(): number {
        return this.fps;
    }
}
